/**
 * Доля ядра публичного рендерера: вид `timer` (блок «Таймер» экрана оплаты).
 *
 * WEB-2921, заход 1: перенесено из `PublicBlockRenderer.tsx` слово в слово.
 * Метка `case 'timer'` остаётся в ядре; тело ветки — функция отрисовки
 * `renderTimer(ctx)`, замыкания ядра приходят объектом-контекстом
 * (`CoreRenderContext`). Хуков здесь нет. Доля едет в первом экране вместе с
 * ядром (статический импорт), ленивого куска у неё нет.
 */
import React from 'react';
// WEB-1692 юнит 3: цифры пейволльного «Таймера» получили тот же набор
// настроек, что цифры его квизового близнеца, а подписи под ними — роль
// `Secondary` общего механизма ролей текста.
import { blockTextRoles } from '../semanticTextRoles';
import { resolveFontPair } from '@/themes/fonts';
// WEB-1013 (возврат QA 09.08): блок «Таймер» (type: 'timer') рендерился
// хардкодом и не знал про stylePackId — формат из пака был подключён только
// к соседнему блоку «Обратный отсчёт» (type: 'countdown', countdown.ts).
import {
  COUNTDOWN_FORMAT_DIGIT_SIZE,
  COUNTDOWN_MONO_FAMILY,
  countdownLabelsFor,
  resolveCountdownFormat,
} from '../countdown';
import type { CoreRenderContext } from '../PublicBlockRenderer';

export function renderTimer({
  block,
  stylePackId,
  publicStrings,
}: CoreRenderContext): React.ReactElement {
  // WEB-1013 (возврат QA 09.08): формат — поле контракта набора
  // (`paywall.timer`), та же функция, что уже используют `countdown` и
  // sticky-хедер. `'none'`/без пака резолвится в 'default' — сегодняшний
  // вид блока не меняется (пак не убивает и не переодевает блок без
  // объявленного формата).
  const totalSeconds = block.timerSeconds ?? 600;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const format = resolveCountdownFormat(stylePackId);
  // WEB-1692 юнит 3 (репорт владельца 24.08 «не хватает настроек выбора
  // шрифта для цифр, выбора цвета и выбора размера»). Накладка автора
  // спредится ПОСЛЕ прежних строк и при пустых полях равна пустому
  // объекту — старая воронка не меняется ни на байт.
  //
  // ⚠ КРАСНЫЙ ОСТАЁТСЯ УМОЛЧАНИЕМ: цвет здесь несёт смысл (таймер
  // срочности на экране оплаты), прежняя строка `var(--color-error,
  // #ff4d4f)` остаётся в исходнике дословно и работает, пока автор не
  // задал свой цвет.
  const timerDigitOverride: React.CSSProperties = {
    ...(block.timerDigitSizePx
      ? { fontSize: block.timerDigitSizePx }
      : {}),
    ...(block.timerDigitColor ? { color: block.timerDigitColor } : {}),
    // Шрифт цифр автора важнее моно-шрифта формата — тот же порядок, что
    // у близнеца (CountdownBlock).
    ...(block.timerDigitFontId
      ? { fontFamily: resolveFontPair(block.timerDigitFontId).headingFamily }
      : {}),
  };
  // Подписи под цифрами — роль `Secondary`; роли `Title` у блока нет.
  const timerLabelOverride = blockTextRoles(block, 'timer').secondary;

  if (format === 'mono-header') {
    return (
      <div
        data-testid="timer"
        data-timer-format={format}
        style={{
          textAlign: 'center',
          fontSize: COUNTDOWN_FORMAT_DIGIT_SIZE[format],
          fontWeight: 700,
          color: 'var(--color-error, #ff4d4f)',
          marginBottom: 16,
          fontFamily: COUNTDOWN_MONO_FAMILY,
          // WEB-1692: накладка автора — ПОСЛЕ прежних строк; пусто = {}.
          ...timerDigitOverride,
        }}
      >
        {String(minutes).padStart(2, '0')}:
        {String(seconds).padStart(2, '0')}
      </div>
    );
  }

  if (format === 'digits-labeled') {
    // Полный словарь формата — [дни, часы, минуты, секунды]; блок
    // «Таймер» короткий (максимум час, только минуты/секунды).
    const labels = countdownLabelsFor(format, publicStrings);
    const digitStyle: React.CSSProperties = {
      fontSize: COUNTDOWN_FORMAT_DIGIT_SIZE[format],
      fontWeight: 700,
      color: 'var(--color-error, #ff4d4f)',
      lineHeight: 1,
      // WEB-1692: накладка автора — ПОСЛЕ прежних строк; пусто = {}.
      ...timerDigitOverride,
    };
    return (
      <div
        data-testid="timer"
        data-timer-format={format}
        style={{
          display: 'flex',
          justifyContent: 'center',
          gap: 16,
          marginBottom: 16,
        }}
      >
        {[minutes, seconds].map((value, i) => (
          <div key={i} style={{ textAlign: 'center' }}>
            <div data-testid="timer-digit" style={digitStyle}>
              {String(value).padStart(2, '0')}
            </div>
            <div
              data-testid="timer-label"
              style={{
                fontSize: 12,
                color: 'var(--color-text-secondary, #8c8c8c)',
                marginTop: 4,
                // WEB-1692: накладка роли `Secondary`; пусто = {}.
                ...timerLabelOverride,
              }}
            >
              {labels[2 + i]}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div
      data-testid="timer"
      data-timer-format="default"
      style={{
        textAlign: 'center',
        fontSize: 36,
        fontWeight: 700,
        color: 'var(--color-error, #ff4d4f)',
        marginBottom: 16,
        // WEB-1692: накладка автора — ПОСЛЕ прежних строк; пусто = {}.
        ...timerDigitOverride,
      }}
    >
      {block.timerSeconds
        ? `${Math.floor(block.timerSeconds / 60)}:${String(block.timerSeconds % 60).padStart(2, '0')}`
        : '10:00'}
    </div>
  );
}
